"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { Star, MessageCircle, Send, Loader2, Sparkles, Pencil, Clock } from "lucide-react";
import { toast } from "sonner";

interface Review {
  id: string;
  rating: number;
  comment: string;
  createdAt: string;
  isApproved: boolean;
  isOwn: boolean;
  canEdit: boolean;
  user: { name: string | null };
}

interface Question {
  id: string;
  askerName: string;
  question: string;
  answer: string | null;
  answeredAt: string | null;
  createdAt: string;
}

export default function ProductReviewsQA({ productId }: { productId: string }) {
  const { data: session } = useSession();
  const [reviews, setReviews] = useState<Review[]>([]);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [loading, setLoading] = useState(true);
  const [canReview, setCanReview] = useState(false);

  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState("");

  const [editingId, setEditingId] = useState<string | null>(null);
  const [editRating, setEditRating] = useState(5);
  const [editComment, setEditComment] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [editError, setEditError] = useState("");

  const [questionText, setQuestionText] = useState("");
  const [askerName, setAskerName] = useState("");
  const [askerEmail, setAskerEmail] = useState("");
  const [submittingQuestion, setSubmittingQuestion] = useState(false);

  const load = async () => {
    try {
      const [reviewsRes, questionsRes] = await Promise.all([
        fetch(`/api/products/${productId}/reviews`).then((r) => r.json()),
        fetch(`/api/products/${productId}/questions`).then((r) => r.json()),
      ]);
      setReviews(Array.isArray(reviewsRes) ? reviewsRes : []);
      setQuestions(Array.isArray(questionsRes) ? questionsRes : []);
    } catch {
      // sessizce geç — bu bölüm sayfanın ana işlevini bloklamamalı
    } finally {
      setLoading(false);
    }
  };

  const loadCanReview = async () => {
    if (!session?.user) { setCanReview(false); return; }
    try {
      const data = await fetch(`/api/products/${productId}/can-review`).then((r) => r.json());
      setCanReview(!!data.canReview);
    } catch {
      setCanReview(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId]);

  useEffect(() => {
    loadCanReview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, session?.user]);

  const handleSubmitReview = async () => {
    setReviewError("");
    if (!comment.trim()) return;
    setSubmittingReview(true);
    try {
      const res = await fetch(`/api/products/${productId}/reviews`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating, comment }),
      });
      const data = await res.json();
      if (res.ok) {
        setComment("");
        setRating(5);
        toast.success("Yorumunuz alındı, incelendikten sonra yayınlanacak. Teşekkürler!");
        load();
        loadCanReview();
      } else {
        setReviewError(data.error || "Yorum eklenemedi");
      }
    } catch {
      setReviewError("Bağlantı hatası, tekrar deneyin");
    } finally {
      setSubmittingReview(false);
    }
  };

  const openEdit = (r: Review) => {
    setEditingId(r.id);
    setEditRating(r.rating);
    setEditComment(r.comment);
    setEditError("");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditError("");
  };

  const handleSaveEdit = async (id: string) => {
    if (!editComment.trim()) return;
    setEditError("");
    setSavingEdit(true);
    try {
      const res = await fetch(`/api/reviews/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rating: editRating, comment: editComment }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Yorumunuz güncellendi, tekrar onay bekliyor.");
        setEditingId(null);
        load();
      } else {
        setEditError(data.error || "Güncellenemedi");
      }
    } catch {
      setEditError("Bağlantı hatası, tekrar deneyin");
    } finally {
      setSavingEdit(false);
    }
  };

  const handleSubmitQuestion = async () => {
    if (!questionText.trim()) return;
    setSubmittingQuestion(true);
    try {
      const res = await fetch(`/api/products/${productId}/questions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: questionText, askerName, askerEmail }),
      });
      if (res.ok) {
        setQuestionText("");
        if (!session?.user) { setAskerName(""); setAskerEmail(""); }
        toast.success("Sorunuz iletildi, en kısa sürede cevaplanacak.");
        load();
      } else {
        toast.error("Soru gönderilemedi, tekrar deneyin");
      }
    } catch {
      toast.error("Bağlantı hatası, tekrar deneyin");
    } finally {
      setSubmittingQuestion(false);
    }
  };

  // Ortalama puan ve toplam sayaç yalnızca ONAYLANMIŞ yorumlardan hesaplanır —
  // kullanıcının kendi onay bekleyen yorumu görünür ama istatistiği etkilemez.
  const approvedReviews = reviews.filter((r) => r.isApproved);
  const avgRating = approvedReviews.length
    ? (approvedReviews.reduce((sum, r) => sum + r.rating, 0) / approvedReviews.length).toFixed(1)
    : null;

  return (
    <div className="mt-24 pt-16 border-t border-[rgba(201,168,76,0.08)] flex flex-col gap-16">
      {/* Soru & Cevap — üstte, ortalı */}
      <section className="w-full max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-3xl text-[#F4F0E8] font-light">Soru & Cevap</h2>
          {questions.length > 0 && (
            <div className="flex items-center gap-1.5 text-[#C9A84C]">
              <MessageCircle size={16} />
              <span className="text-sm font-medium">{questions.length}</span>
              <span className="text-[#7A7670] text-xs">soru</span>
            </div>
          )}
        </div>

        {/* Yeni Soru Sor — daima üstte, soru olsun olmasın aynı yerde */}
        <div className="bg-[#111114] border border-[rgba(201,168,76,0.12)] rounded-xl p-5 space-y-3">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-full bg-[#C9A84C]/10 flex items-center justify-center">
              <MessageCircle size={12} className="text-[#C9A84C]" />
            </div>
            <p className="text-[10px] text-[#C9A84C] uppercase tracking-widest font-bold">Yeni Soru Sor</p>
          </div>
          {!session?.user && (
            <div className="grid grid-cols-2 gap-3">
              <input
                value={askerName}
                onChange={(e) => setAskerName(e.target.value)}
                placeholder="Adınız (opsiyonel)"
                className="bg-black/20 border border-[rgba(201,168,76,0.12)] text-[#F4F0E8] px-4 py-2.5 rounded-lg text-sm focus:outline-none focus:border-[#C9A84C]"
              />
              <input
                value={askerEmail}
                onChange={(e) => setAskerEmail(e.target.value)}
                placeholder="E-posta (opsiyonel)"
                className="bg-black/20 border border-[rgba(201,168,76,0.12)] text-[#F4F0E8] px-4 py-2.5 rounded-lg text-sm focus:outline-none focus:border-[#C9A84C]"
              />
            </div>
          )}
          <textarea
            value={questionText}
            onChange={(e) => setQuestionText(e.target.value)}
            rows={3}
            placeholder="Bu ürünle ilgili merak ettiğinizi sorun..."
            className="w-full bg-black/20 border border-[rgba(201,168,76,0.12)] text-[#F4F0E8] px-4 py-3 rounded-lg text-sm focus:outline-none focus:border-[#C9A84C] resize-none"
          />
          <button
            onClick={handleSubmitQuestion}
            disabled={submittingQuestion || !questionText.trim()}
            className="flex items-center gap-2 bg-[#C9A84C] text-black px-5 py-2.5 rounded font-medium hover:bg-[#D4B664] disabled:opacity-40 transition text-sm"
          >
            {submittingQuestion ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
            Sor
          </button>
        </div>

        {loading ? (
          <Loader2 className="animate-spin text-[#C9A84C]" size={20} />
        ) : (
          <div className="space-y-4">
            {questions.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center border border-dashed border-[rgba(201,168,76,0.15)] rounded-xl">
                <MessageCircle size={26} className="text-[#7A7670]/30" />
                <p className="text-[#7A7670] text-sm italic">Henüz soru sorulmamış — ilk soruyu siz sorun.</p>
              </div>
            ) : (
              questions.map((q) => (
                <div key={q.id} className="bg-[#111114] border border-[rgba(201,168,76,0.08)] rounded-xl p-5 space-y-3 hover:border-[rgba(201,168,76,0.2)] transition-colors">
                  <div className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-full bg-[#1A1A1F] border border-[rgba(201,168,76,0.2)] flex items-center justify-center text-[#C9A84C] text-xs font-bold uppercase shrink-0">
                      {q.askerName?.[0] || "?"}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <span className="text-[#F4F0E8] text-xs font-semibold">{q.askerName}</span>
                        <span className="text-[9px] text-[#7A7670]/60 uppercase tracking-widest shrink-0">
                          {new Date(q.createdAt).toLocaleDateString("tr-TR")}
                        </span>
                      </div>
                      <p className="text-[#F4F0E8] text-sm mt-1 leading-relaxed">{q.question}</p>
                    </div>
                  </div>

                  {q.answer ? (
                    <div className="ml-11 flex items-start gap-3 pt-3 border-t border-[rgba(201,168,76,0.08)]">
                      <div className="w-8 h-8 rounded-full bg-[#C9A84C]/10 border border-[#C9A84C]/40 flex items-center justify-center shrink-0">
                        <Sparkles size={13} className="text-[#C9A84C]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[10px] text-[#C9A84C] font-bold uppercase tracking-widest">Parfume Ekibi</span>
                        <p className="text-[#7A7670] text-sm mt-1 leading-relaxed">{q.answer}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="ml-11 flex items-center gap-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                      <span className="text-[9px] text-amber-400/80 uppercase tracking-widest font-medium">Yanıt Bekleniyor</span>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* Değerlendirmeler — en altta, ortalı */}
      <section id="degerlendirmeler" className="w-full max-w-2xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="font-serif text-3xl text-[#F4F0E8] font-light">
            Değerlendirmeler
          </h2>
          {avgRating && (
            <div className="flex items-center gap-1.5 text-[#C9A84C]">
              <Star size={16} fill="currentColor" />
              <span className="text-sm font-medium">{avgRating}</span>
              <span className="text-[#7A7670] text-xs">({approvedReviews.length})</span>
            </div>
          )}
        </div>

        {loading ? (
          <Loader2 className="animate-spin text-[#C9A84C]" size={20} />
        ) : (
          <div className="space-y-4">
            {reviews.length === 0 ? (
              <div className="flex flex-col items-center gap-3 py-10 text-center border border-dashed border-[rgba(201,168,76,0.15)] rounded-xl">
                <Star size={26} className="text-[#7A7670]/30" />
                <p className="text-[#7A7670] text-sm italic">Henüz yorum yapılmamış — ilk yorumu siz yazın.</p>
              </div>
            ) : (
              reviews.map((r) => (
                <div key={r.id} className="bg-[#111114] border border-[rgba(201,168,76,0.08)] rounded-xl p-5 space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[#F4F0E8] text-sm font-medium">{r.user.name || "Müşteri"}</span>
                      {!r.isApproved && (
                        <span className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-widest bg-amber-500/10 text-amber-400 border border-amber-500/25 px-2 py-0.5 rounded-full">
                          <Clock size={9} /> Onay Bekliyor
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-0.5">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <Star key={i} size={13} className={i < r.rating ? "text-[#C9A84C]" : "text-[#7A7670]/30"} fill={i < r.rating ? "currentColor" : "none"} />
                      ))}
                    </div>
                  </div>

                  {editingId === r.id ? (
                    <div className="space-y-3 pt-1">
                      <div className="flex items-center gap-1">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <button key={i} type="button" onClick={() => setEditRating(i + 1)}>
                            <Star size={18} className={i < editRating ? "text-[#C9A84C]" : "text-[#7A7670]/30"} fill={i < editRating ? "currentColor" : "none"} />
                          </button>
                        ))}
                      </div>
                      <textarea
                        value={editComment}
                        onChange={(e) => setEditComment(e.target.value)}
                        rows={3}
                        className="w-full bg-black/20 border border-[rgba(201,168,76,0.12)] text-[#F4F0E8] px-4 py-3 rounded-lg text-sm focus:outline-none focus:border-[#C9A84C] resize-none"
                      />
                      {editError && <p className="text-xs text-red-400">{editError}</p>}
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleSaveEdit(r.id)}
                          disabled={savingEdit || !editComment.trim()}
                          className="flex items-center gap-2 bg-[#C9A84C] text-black px-4 py-2 rounded text-xs font-bold uppercase tracking-widest hover:bg-[#D4B664] disabled:opacity-40 transition"
                        >
                          {savingEdit ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                          Güncelle
                        </button>
                        <button
                          onClick={cancelEdit}
                          className="text-[#7A7670] text-xs font-bold uppercase tracking-widest hover:text-[#F4F0E8] transition"
                        >
                          Vazgeç
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <p className="text-[#7A7670] text-sm leading-relaxed">{r.comment}</p>
                      <div className="flex items-center justify-between">
                        <p className="text-[9px] text-[#7A7670]/60 uppercase tracking-widest">
                          {new Date(r.createdAt).toLocaleDateString("tr-TR")}
                        </p>
                        {r.canEdit && (
                          <button
                            onClick={() => openEdit(r)}
                            className="flex items-center gap-1.5 text-[9px] text-[#C9A84C] hover:underline uppercase font-bold tracking-widest"
                          >
                            <Pencil size={10} /> Düzenle
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              ))
            )}
          </div>
        )}

        {/* Yorum ekleme formu — yalnızca bu ürünü TESLİM ALMIŞ kullanıcıya gösterilir, aksi halde hiçbir şey gösterilmez */}
        {canReview && (
          <div className="bg-[#111114] border border-[rgba(201,168,76,0.12)] rounded-xl p-5 space-y-3">
            <p className="text-[10px] text-[#C9A84C] uppercase tracking-widest font-bold">Yorum Yaz</p>
            <div className="flex items-center gap-1">
              {Array.from({ length: 5 }).map((_, i) => (
                <button key={i} type="button" onClick={() => setRating(i + 1)}>
                  <Star size={20} className={i < rating ? "text-[#C9A84C]" : "text-[#7A7670]/30"} fill={i < rating ? "currentColor" : "none"} />
                </button>
              ))}
            </div>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={3}
              placeholder="Bu ürün hakkındaki deneyiminizi paylaşın..."
              className="w-full bg-black/20 border border-[rgba(201,168,76,0.12)] text-[#F4F0E8] px-4 py-3 rounded-lg text-sm focus:outline-none focus:border-[#C9A84C] resize-none"
            />
            {reviewError && <p className="text-xs text-red-400">{reviewError}</p>}
            <button
              onClick={handleSubmitReview}
              disabled={submittingReview || !comment.trim()}
              className="flex items-center gap-2 bg-[#C9A84C] text-black px-5 py-2.5 rounded font-medium hover:bg-[#D4B664] disabled:opacity-40 transition text-sm"
            >
              {submittingReview ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              Gönder
            </button>
          </div>
        )}
      </section>
    </div>
  );
}
